import { isPlausibleHlc } from '../../services/sync/hlc'
import type { CorpusNode } from './model'

// Finding, walking and copying nodes in a corpus tree. None of these touch
// the store; they take the tree they work on.

export function generateId(subtype: 'desk' | 'folder' | 'page' = 'page'): string {
  return `${subtype}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`
}

/**
 * Copy of a tree without lazily-created pages that were never edited. Those
 * drafts live only in memory; anything written to disk must leave them out,
 * or they come back on the next launch as permanent "Untitled" pages.
 */
export function withoutUnsavedNodes(nodes: CorpusNode[]): CorpusNode[] {
  return nodes
    .filter((node) => !node.isUnsaved)
    .map((node) => node.children
      ? { ...node, children: withoutUnsavedNodes(node.children) }
      : node)
}

export function findNode(nodes: CorpusNode[], id: string): CorpusNode | null {
  for (const node of nodes) {
    if (node.id === id) return node
    if (node.children) {
      const found = findNode(node.children, id)
      if (found) return found
    }
  }
  return null
}

/** Returns the complete root-to-node path, including the requested node. */
export function findNodePath(nodes: CorpusNode[], id: string): CorpusNode[] {
  for (const node of nodes) {
    if (node.id === id) return [node]
    if (node.children?.length) {
      const childPath = findNodePath(node.children, id)
      if (childPath.length) return [node, ...childPath]
    }
  }
  return []
}

export function findParentList(
  nodes: CorpusNode[],
  id: string
): { list: CorpusNode[]; index: number; parent: CorpusNode | null } | null {
  for (let i = 0; i < nodes.length; i++) {
    if (nodes[i].id === id) {
      return { list: nodes, index: i, parent: null }
    }
    if (nodes[i].children) {
      const res = findParentListInternal(nodes[i].children!, id, nodes[i])
      if (res) return res
    }
  }
  return null
}

function findParentListInternal(
  nodes: CorpusNode[],
  id: string,
  parent: CorpusNode
): { list: CorpusNode[]; index: number; parent: CorpusNode } | null {
  for (let i = 0; i < nodes.length; i++) {
    if (nodes[i].id === id) {
      return { list: nodes, index: i, parent }
    }
    if (nodes[i].children) {
      const res = findParentListInternal(nodes[i].children!, id, nodes[i])
      if (res) return res
    }
  }
  return null
}

export function isDescendantNode(parent: CorpusNode, targetId: string): boolean {
  if (!parent.children || parent.children.length === 0) return false
  for (const child of parent.children) {
    if (child.id === targetId) return true
    if (isDescendantNode(child, targetId)) return true
  }
  return false
}

export function findFirstPage(nodes: CorpusNode[]): CorpusNode | null {
  for (const node of nodes) {
    if (node.subtype === 'page') return node
    if (node.children) {
      const found = findFirstPage(node.children)
      if (found) return found
    }
  }
  return null
}

/** `ids` sorted into the order they appear in the tree (depth first). */
export function nodeIdsInTreeOrder(nodes: CorpusNode[], ids: string[]): string[] {
  const wanted = new Set(ids)
  return flattenNodes(nodes).map(({ node }) => node.id).filter((id) => wanted.has(id))
}

export function flattenNodes(nodes: CorpusNode[], parentId: string | null = null): Array<{ node: CorpusNode; parentId: string | null }> {
  const flattened: Array<{ node: CorpusNode; parentId: string | null }> = []
  for (const node of nodes) {
    flattened.push({ node, parentId })
    if (node.children) flattened.push(...flattenNodes(node.children, node.id))
  }
  return flattened
}

export function pruneDescendantNodeIds(nodes: CorpusNode[], ids: string[]): string[] {
  const idSet = new Set(ids)
  return ids.filter((id) => {
    const path = findNodePath(nodes, id)
    return !path.slice(0, -1).some((ancestor) => idSet.has(ancestor.id))
  })
}

export function getVisibleNodeIds(nodes: CorpusNode[], includeDeskChildren = false): string[] {
  const result: string[] = []
  for (const node of nodes) {
    result.push(node.id)
    if ((includeDeskChildren || node.subtype !== 'desk') && node.expanded && node.children?.length) {
      result.push(...getVisibleNodeIds(node.children, includeDeskChildren))
    }
  }
  return result
}

/**
 * Clears node timestamps that came from a broken clock, so the cloud's copy
 * wins the next pull instead of every later change being refused locally.
 */
export function repairNodeHlcs(nodes: CorpusNode[]): boolean {
  let repaired = false
  for (const { node } of flattenNodes(nodes)) {
    if (node.hlcTimestamp && !isPlausibleHlc(node.hlcTimestamp)) {
      node.hlcTimestamp = 0
      repaired = true
    }
  }
  return repaired
}
